import { useEffect, useState } from "react";
import { Phone, History } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useCallTracker } from "@/components/app/CallTracker";

/**
 * Lead-centric CRM-style call timeline. Shown inside the customer profile
 * modal so telecallers see EVERY tracked call for THIS lead at the top of
 * the profile — not a global log. Auto-refreshes whenever a new call log
 * is submitted through the CallTracker.
 */
export default function LeadCallHistory({ leadId }) {
  const { finishedTick, active } = useCallTracker();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) return;
    let alive = true;
    setLoading(true);
    api.get(`/staff/call-logs/lead/${leadId}`)
      .then(r => { if (alive) setCalls(r.data.calls || []); })
      .catch(() => { if (alive) setCalls([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // finishedTick bumps whenever ANY call finishes → re-fetch to pick up new row
  }, [leadId, finishedTick, active?.id]);

  const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return "—"; } };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); } catch { return ""; } };
  const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("en-IN"); } catch { return iso; } };
  const ago = (iso) => {
    if (!iso) return "";
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };
  const bandCls = (c) => {
    if (c.status === "in_progress") return "bg-red-500/20 text-red-200 border-red-500/40 animate-pulse";
    if (c.is_fake) return "bg-red-500/15 text-red-300 border-red-500/40";
    if (c.band === "short") return "bg-amber-500/15 text-amber-300 border-amber-500/40";
    if (c.band === "valid") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
    return "bg-slate-700/40 text-slate-300 border-slate-600/50";
  };
  const outcomeCls = (c) =>
    c.is_fake ? "text-red-300" : c.status === "in_progress" ? "text-red-300"
    : c.band === "short" ? "text-amber-300" : "text-emerald-300";

  const lastCompleted = calls.find(c => c.status === "completed");

  return (
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="lead-call-history">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold flex items-center gap-1.5">
          <Phone className="w-3 h-3"/> Call History for this Lead
        </div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1">
          <History className="w-3 h-3"/> {calls.length} calls
        </div>
      </div>

      {lastCompleted && (
        <div className="mb-3 text-xs text-slate-300" data-testid="lead-last-call-line">
          Last call: <span className="text-white font-semibold">{ago(lastCompleted.end_time)}</span>
          <span className="text-slate-500"> · </span>
          <span className="font-mono-alt text-white">{lastCompleted.duration_formatted}</span>
          <span className="text-slate-500"> · </span>
          <span className={outcomeCls(lastCompleted) + " font-semibold"}>{lastCompleted.outcome}</span>
        </div>
      )}

      {loading && <div className="text-xs text-slate-500">Loading call history…</div>}
      {!loading && calls.length === 0 && (
        <div className="text-xs text-slate-500 italic" data-testid="lead-calls-empty">
          No calls tracked yet for this lead — start one from the CALL button on the lead card.
        </div>
      )}

      {!loading && calls.length > 0 && (
        <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
          {(() => {
            // Group calls by IST day for cross-session clarity
            const groups = [];
            let curKey = null;
            for (const c of calls) {
              const d = new Date(c.start_time);
              const key = d.toDateString();
              if (key !== curKey) { groups.push({ key, label: dayLabel(d), items: [] }); curKey = key; }
              groups[groups.length - 1].items.push(c);
            }
            return groups.map(g => (
              <div key={g.key} className="space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold sticky top-0 bg-emerald-500/5 py-0.5" data-testid={`day-header-${g.key}`}>
                  {g.label}
                </div>
                {g.items.map(c => (
                  <div key={c.id} className="relative pl-4 border-l-2 border-slate-700" data-testid={`lead-call-row-${c.id}`}>
                    <div className={`absolute -left-[5px] top-1 w-2 h-2 rounded-full ${c.is_fake ? "bg-red-400" : c.status === "in_progress" ? "bg-red-400 animate-pulse" : c.band === "short" ? "bg-amber-400" : "bg-emerald-400"}`}/>
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="text-slate-400 font-mono-alt">{fmtTime(c.start_time)}</span>
                      <span className="text-slate-600">·</span>
                      <span className="text-white font-semibold">{c.telecaller_name || "—"}</span>
                      <Badge className={"text-[10px] uppercase tracking-widest border font-mono-alt " + bandCls(c)}>
                        {c.status === "in_progress" ? "LIVE" : (c.duration_formatted || "—")}
                      </Badge>
                      <Badge className={"text-[10px] uppercase tracking-widest border border-slate-700 bg-slate-950 " + outcomeCls(c)}>
                        {c.outcome || "—"}
                      </Badge>
                    </div>
                    {c.remark && (
                      <div className="text-[11px] text-slate-300 mt-1 italic pl-0.5" data-testid={`lead-call-remark-${c.id}`}>
                        "{c.remark}"
                      </div>
                    )}
                    {c.next_followup && (
                      <div className="text-[10px] text-blue-300 mt-1 pl-0.5">
                        ➜ Follow-up: {fmtDT(c.next_followup)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>
      )}
    </div>
  );
}

function dayLabel(d) {
  const today = new Date();
  const y = new Date(); y.setDate(y.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "Today";
  if (same(d, y)) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
